import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Search, RefreshCw } from "lucide-react";

/**
 * Admin-only tab: list every Discord-linked user and their verification
 * status. Admins can manually stamp `is_verified=true` (also assigns the
 * Members role on Discord) or revoke it.
 */
export default function VerificationAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");   // all | verified | unverified

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/verified-users");
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load users");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const users = data?.users || [];
  const filtered = useMemo(() => {
    let list = users;
    if (filter === "verified") list = list.filter((u) => u.is_verified);
    if (filter === "unverified") list = list.filter((u) => !u.is_verified);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((u) =>
        (u.discord_username || "").toLowerCase().includes(needle)
        || (u.discord_id || "").includes(needle)
        || (u.email || "").toLowerCase().includes(needle));
    }
    return list;
  }, [users, filter, q]);

  const verify = async (id) => {
    setBusyId(id);
    try {
      const { data: r } = await api.post(`/admin/users/${id}/verify`);
      toast.success(`Verified · role: ${r?.role_grant?.action || r?.role_grant?.reason || "n/a"}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusyId(null); }
  };

  const unverify = async (id) => {
    if (!window.confirm("Revoke verification for this user? The Members role will also be removed from Discord.")) return;
    setBusyId(id);
    try {
      await api.post(`/admin/users/${id}/unverify`);
      toast.success("Verification revoked");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusyId(null); }
  };

  return (
    <div data-testid="verification-admin-tab">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="label-eyebrow text-[var(--ff-red)] mb-2 flex items-center gap-2">
            <ShieldCheck size={12} /> ▮ VERIFICATION
          </div>
          <h2 className="font-display text-3xl">Verified Users</h2>
          {data && (
            <p className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mt-2">
              {data.verified_count} of {data.total} Discord-linked users verified
            </p>
          )}
        </div>
        <button onClick={load} className="btn-ghost text-xs self-start" data-testid="verify-admin-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ff-text-dim)]" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, Discord ID, or email…"
            className="w-full pl-10 pr-4 py-3 text-sm"
            data-testid="verify-admin-search"
          />
        </div>
        <div className="flex gap-px bg-[var(--ff-border)]">
          {["all", "verified", "unverified"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-3 font-mono-ui text-xs uppercase tracking-[0.15em] ${filter === f ? "bg-[var(--ff-red)] text-white" : "bg-[var(--ff-surface)] text-[var(--ff-text-dim)] hover:text-white"}`}
              data-testid={`verify-admin-filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading && !data && <div className="text-[var(--ff-text-dim)] text-sm">Loading…</div>}

      <div className="surface-card overflow-hidden">
        <div className="hidden md:grid grid-cols-[minmax(0,3fr)_minmax(0,2fr)_minmax(0,2fr)_180px] gap-4 px-4 py-3 border-b border-[var(--ff-border)] font-mono-ui text-[0.6rem] uppercase tracking-[0.2em] text-[var(--ff-text-dim)]">
          <div>User</div>
          <div>Status</div>
          <div>Roles</div>
          <div className="text-right">Actions</div>
        </div>
        <div className="divide-y divide-[var(--ff-border)]">
          {filtered.map((u) => (
            <div
              key={u.id}
              className="grid md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)_minmax(0,2fr)_180px] gap-4 px-4 py-3 items-center"
              data-testid={`verify-admin-row-${u.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {u.discord_avatar
                  ? <img src={u.discord_avatar} alt="" className="w-9 h-9 rounded-full shrink-0" />
                  : <div className="w-9 h-9 rounded-full bg-[var(--ff-red)] text-white flex items-center justify-center text-xs shrink-0">?</div>}
                <div className="min-w-0">
                  <div className="text-sm truncate">{u.discord_username || u.email}</div>
                  <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] truncate">
                    ID {u.discord_id}
                  </div>
                </div>
              </div>
              <div>
                {u.is_verified ? (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 border border-green-500/50 text-green-300 font-mono-ui text-[0.6rem] uppercase tracking-[0.15em]" data-testid={`verify-admin-status-${u.id}`}>
                    <ShieldCheck size={11} /> Verified {u.verified_by_admin && "· by admin"}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 border border-[var(--ff-border)] text-[var(--ff-text-dim)] font-mono-ui text-[0.6rem] uppercase tracking-[0.15em]" data-testid={`verify-admin-status-${u.id}`}>
                    Not verified
                  </span>
                )}
                {u.is_inactive && (
                  <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 border border-yellow-500/40 text-yellow-300 font-mono-ui text-[0.55rem] uppercase tracking-[0.15em]">
                    Inactive
                  </span>
                )}
              </div>
              <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
                {(u.roles || [u.role]).filter(Boolean).join(" · ") || "—"}
              </div>
              <div className="flex justify-end gap-2">
                {u.is_verified ? (
                  <button
                    onClick={() => unverify(u.id)}
                    disabled={busyId === u.id}
                    className="btn-ghost text-xs"
                    data-testid={`verify-admin-unverify-${u.id}`}
                  >
                    <ShieldOff size={12} /> Revoke
                  </button>
                ) : (
                  <button
                    onClick={() => verify(u.id)}
                    disabled={busyId === u.id}
                    className="btn-primary text-xs"
                    data-testid={`verify-admin-verify-${u.id}`}
                  >
                    <ShieldCheck size={12} /> Verify
                  </button>
                )}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="px-4 py-8 text-center text-[var(--ff-text-dim)] font-mono-ui text-sm" data-testid="verify-admin-empty">
              No users match this filter.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
